import type {AnalysisOptionProject, ProjectToAnalyze} from "../AnalysisOptions.ts";
import {resolveSetterVal, type Setter, type SetterVal} from "../../../util/setter/Setter.ts";
import {useQuery} from "@tanstack/react-query";
import {useAtomValue} from "jotai";
import {ApiAtom} from "../../../common-process/1-metl-connection/MetlConnectionProperties.ts";
import {SuspenseLayer} from "../../../util/suspense/SuspenseLayer.tsx";
import Styles from "./ProjectSelection.module.scss"
import {CheckboxField} from "../../../components/checkbox-field/CheckboxField.tsx";

interface SpecificVersionProps {
    selectedProject: { groupName: string, projectName: string }
    value: ProjectToAnalyze | undefined
    setter: Setter<ProjectToAnalyze | undefined>
}
function SpecificVersionSelector({selectedProject, value, setter}: SpecificVersionProps) {
    const api = useAtomValue(ApiAtom)
    const {data: versions} = useQuery({
        queryKey: [api?.id ?? "api-key", "versions", selectedProject.groupName, selectedProject.projectName],
        queryFn: async () => {
            if(!api) return []
            const versions = await api.groups.get(selectedProject.groupName)
                .projects.get(selectedProject.projectName)
                .versions.list()
            return versions
        }
    })
    const toggleVersion = (version: string) => {
        setter(old => old ? {
            ...old,
            versionsToAnalyze: old.versionsToAnalyze.includes(version) ?
                old.versionsToAnalyze.filter(v => v !== version) :
                [...old.versionsToAnalyze, version]
        } : { versionsToAnalyze: [version], groupName: selectedProject.groupName, projectName: selectedProject.projectName, analyzeAllVersions: false})
    }
    const clear = () => {
        setter(undefined)
    }
    return <div>
        <div className={Styles.Title}>
            <h4>Specific Versions</h4>
            {(value?.versionsToAnalyze?.length ?? 0) > 0 &&<button onClick={clear}>clear</button>}
        </div>
        <div className={Styles.Projects}>
            {versions?.map(version => <div key={version} className={Styles.Project}>
                <span>
                    {version}
                </span>
                <input type="checkbox"
                       checked={value?.versionsToAnalyze.includes(version) ?? false}
                       onChange={() => toggleVersion(version)}
                       />
            </div>)}
        </div>
    </div>
}

interface Props {
    selectedProject: { groupName: string, projectName: string } | undefined
    setSelectedProject: Setter<{ groupName: string, projectName: string } | undefined>
    project: AnalysisOptionProject
    setProject: Setter<AnalysisOptionProject>
}
export function VersionSelector({selectedProject, setSelectedProject, project, setProject}: Props) {
    const value = project.projectsToAnalyze.find(s => s.groupName === selectedProject?.groupName && s.projectName === selectedProject?.projectName)
    const setValue = (newValue: SetterVal<ProjectToAnalyze | undefined>) => {
        setProject(old => {
            const existingIndex = old.projectsToAnalyze.findIndex(s => s.groupName === selectedProject?.groupName && s.projectName === selectedProject?.projectName)
            const existing = existingIndex === -1 ? undefined : old.projectsToAnalyze[existingIndex]
            const newResolvedValue = resolveSetterVal(existing, newValue)
            if(newResolvedValue === existing) return old
            const newArray = [...old.projectsToAnalyze]
            if(existing === undefined && newResolvedValue !== undefined) {
                newArray.push(newResolvedValue)
            } else if(newResolvedValue === undefined) {
                newArray.splice(existingIndex, 1)
            } else {
                newArray[existingIndex] = newResolvedValue
            }
            return {
                ...old,
                projectsToAnalyze: newArray
            }
        })
    }
    const changeAnalyseAll = (newValue: boolean) => {
        setValue(old => ({
            analyzeAllVersions: newValue,
            versionsToAnalyze: old?.versionsToAnalyze ?? [],
            groupName: selectedProject?.groupName ?? "Ah", projectName: selectedProject?.projectName ?? "Ah"
        }))
    }

    if(!selectedProject) return <div><h3>Please select a project</h3></div>
    return <>
        <div className={Styles.Title}>
            <h3>{selectedProject.groupName}/{selectedProject.projectName}</h3>
            <button onClick={()=>setSelectedProject(undefined)}>X</button>
        </div>
        <CheckboxField
            title="Analyze all versions" small
            description={value?.analyzeAllVersions ? "This will analyze all versions in this project, not recommended for projects with lots of similar versions" : undefined }
            value={value?.analyzeAllVersions ?? false}
            setValue={changeAnalyseAll} />
        {selectedProject && !value?.analyzeAllVersions && <SuspenseLayer>
            <SpecificVersionSelector selectedProject={selectedProject} value={value} setter={setValue} />
        </SuspenseLayer>}
    </>
}